// src/utils/calcModifyAddonsPreview.ts

// ---------------------------------------------
// Helpers for Modify Add-ons page (NO UI logic)
// ---------------------------------------------

import { round2 } from "utils/calc";
import { calcBookingBreakdown } from "utils/calcBookingBreakdown";

type EditedAddonUI = {
  id: string; // addOnId (same as bookingAddOn.addOnId in options list)
  quantity: number;
  numberOfNights?: number;
};

const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v));

/**
 * Inject updated add-ons into a cloned trip.
 * We DO NOT compute money here. We only reshape trip data so calcBookingBreakdown can price it.
 *
 */
type BookingAddOnOptionLike = {
  addOnId: string;
  label?: string;
  title?: string;
  pricePerNight?: number;
  isOneTimePriced?: boolean;
};

export const applyEditedAddonsToTrip = (
  trip: any,
  bookingId: string,
  editedAddons: EditedAddonUI[],
  applyToAll: boolean,
  fallbackNights: number,
  bookingAddOnsOptions: BookingAddOnOptionLike[] = []
) => {
  if (!trip) return trip;

  const t = clone(trip);

  const optionById: Record<string, BookingAddOnOptionLike> = {};
  for (let i = 0; i < bookingAddOnsOptions.length; i++) {
    const o = bookingAddOnsOptions[i];
    if (o?.addOnId) optionById[String(o.addOnId)] = o;
  }

  const replaceAddonsForBooking = (booking: any) => {
    const bookingNights =
      booking?.roomBookings?.[0]?.numberOfNights ?? fallbackNights ?? 1;

    // Existing addons indexed by bookingAddOn.id
    const existing = Array.isArray(booking?.addons) ? booking.addons : [];
    const map = new Map<string, any>();

    for (const a of existing) {
      if (a?.isDeleted) continue;
      // normalize key = addOnId (some payloads have id, some have addOnId)
      const addonKey = String(a?.bookingAddOn?.addOnId || a?.bookingAddOn?.id || "");
      if (addonKey) map.set(addonKey, a);
    }

    // Apply edited state
    for (const e of editedAddons) {
      const addonKey = String(e.id);
      const qty = Number(e.quantity || 0);

      const nightsRaw = Number(e.numberOfNights || 0);
      const nights = nightsRaw > 0 ? nightsRaw : bookingNights;

      const prev = map.get(addonKey);

      if (qty <= 0) {
        // remove
        if (prev) map.delete(addonKey);
        continue;
      }

      if (prev) {
        // Existing addon: keep original bookingAddOn (it already has pricing), only update values
        map.set(addonKey, {
          ...prev,
          isDeleted: false,
          quantity: qty,
          numberOfNights: nights,
        });
      } else {
        // New addon: inject pricing from options list so calcBookingBreakdown can price it
        const opt = optionById[addonKey];

        map.set(addonKey, {
          bookingAddOn: {
            id: addonKey,
            addOnId: addonKey,
            title: opt?.title,
            label: opt?.label,
            pricePerNight: Number(opt?.pricePerNight || 0),
            isOneTimePriced: !!opt?.isOneTimePriced,
          },
          quantity: qty,
          numberOfNights: nights,
          isDeleted: false,
        });
      }
    }

    booking.addons = Array.from(map.values());
  };

  for (const b of t?.bookings || []) {
    const shouldApply = applyToAll
      ? !b?.isDeleted && b?.status !== "CANCELLED"
      : b?.bookingId === bookingId;
  
    if (!shouldApply) continue;
    replaceAddonsForBooking(b);
  }

  return t;
};

export type OverviewAddonDelta = {
  key: string;
  title: string;
  delta: number;
};

export const diffAddonsOverview = (
  base: any,
  next: any
): OverviewAddonDelta[] => {
  const b0 = (base?.lines || []) as any[];
  const b1 = (next?.lines || []) as any[];

  const onlyAddons = (lines: any[]) =>
    lines.filter((l) => String(l?.key || "").startsWith("addon_"));

  const m0 = new Map<string, any>();
  const m1 = new Map<string, any>();

  for (let i = 0; i < onlyAddons(b0).length; i++) {
    const l = onlyAddons(b0)[i];
    if (l?.key) m0.set(String(l.key), l);
  }

  for (let i = 0; i < onlyAddons(b1).length; i++) {
    const l = onlyAddons(b1)[i];
    if (l?.key) m1.set(String(l.key), l);
  }

  // Collect keys without spreading iterators (ES5-safe)
  const allKeysArr: string[] = [];
  const seen: Record<string, true> = {};

  m0.forEach((_v, k) => {
    const kk = String(k);
    if (!seen[kk]) {
      seen[kk] = true;
      allKeysArr.push(kk);
    }
  });

  m1.forEach((_v, k) => {
    const kk = String(k);
    if (!seen[kk]) {
      seen[kk] = true;
      allKeysArr.push(kk);
    }
  });

  const out: OverviewAddonDelta[] = [];

  for (let i = 0; i < allKeysArr.length; i++) {
    const k = allKeysArr[i];

    const a = m0.get(k);
    const b = m1.get(k);

    const delta = round2(Number(b?.amount || 0) - Number(a?.amount || 0));
    if (delta === 0) continue;

    out.push({
      key: k,
      title: String((b || a)?.title || "Add-on"),
      delta,
    });
  }

  out.sort((x, y) => Math.abs(y.delta) - Math.abs(x.delta));
  return out;
};

export type ModifyAddonsPreviewInput = {
  trip: any;
  bookingId: string;
  tripAddOnsUpdated: EditedAddonUI[];
  bookingAddOns: BookingAddOnOptionLike[];
  bookingNights: number;
  applyToAll: boolean;
  hasUserChangedAddons: boolean;
};

export const calcModifyAddonsPreview = ({
  trip,
  bookingId,
  tripAddOnsUpdated,
  bookingAddOns,
  bookingNights,
  applyToAll,
  hasUserChangedAddons,
}: ModifyAddonsPreviewInput) => {
  if (!trip) return null;
  if (!bookingId) return null;
  if (!hasUserChangedAddons) return null;

  const nextTrip = applyEditedAddonsToTrip(
    trip,
    bookingId,
    tripAddOnsUpdated,
    applyToAll,
    bookingNights,
    bookingAddOns
  );

  return calcBookingBreakdown(nextTrip);
};